import defaultTheme from "tailwindcss/defaultTheme";
import colors from "tailwindcss/colors";
import plugin from "tailwindcss/plugin";
import fluid, { extract } from "fluid-tailwind";
import forms from "@tailwindcss/forms";
import containerQueries from "@tailwindcss/container-queries";
import { convertToRem, generateSteps } from "./tailwind-plugins/_helpers";
import generateFontSize from "./tailwind-plugins/fontSize";

const config = {
	important: "#body",
	content: {
		files: ["./web/**/*.{html,php}", "./src/js/**/*.{js,vue}"],
		extract,
	},
	theme: {
		toRem: convertToRem(10),
		extend: {
			maxHeight: {
				screen: 'calc(var(--vh, 1vh) * 100)',
			},
			maxWidth: {
				'screen': '100vw',
			},
			minHeight: {
				auto: 'auto',
				screen: 'calc(var(--vh, 1vh) * 100)',
			},
			height: {
				screen: 'calc(var(--vh, 1vh) * 100)',
			},
			outlineOffset: {
				3: '3px',
			}
		},
		backdropBlur: {
			sm: '5px',
			lg: '8px',
		},
		borderRadius: {
			0: 0,
			4: convertToRem(4),
			6: convertToRem(6),
			8: convertToRem(8),
			10: convertToRem(10),
			12: convertToRem(12),
			14: convertToRem(14),
			16: convertToRem(16),
			24: convertToRem(24),
			100: convertToRem(100),
			full: '100%'
		},
		boxShadow: {
			DEFAULT:  `0px 4px 4px 0px rgb(0 0 0 / 0.25)`,
		},
		colors: {
			transparent: "transparent",
			current: "currentColor",
			inherit: "inherit",
			black: "#000000",
			white: "#ffffff",
			spruce: {
				950: '#080f08',
				900: '#151d15',
				800: '#212b22',
				700: '#3b4a36',
				600: '#58664d',
				500: '#748463',
				400: '#909d82',
				300: '#acb5a1',
				200: '#c7cec1',
				100: '#e3e6e0',
				50: '#f1f3f0',
			},
			sun: {
				950: '#140c01',
				900: '#281801',
				800: '#4f2f02',
				700: '#764d14',
				600: '#9d6c25',
				500: '#c48a37',
				400: '#e3b15b',
				300: '#eac484',
				200: '#f1d8ad',
				100: '#f8ecd6',
				50: '#fcf6eb',
			},
			clay: {
				950: '#160c07',
				900: '#2b180e',
				800: '#543221',
				700: '#7c4d34',
				600: '#a56747',
				500: '#ba785b',
				400: '#c8937c',
				300: '#d6ae9d',
				200: '#e3c9bd',
				100: '#f1e4de',
				50: '#f8f2ef',
			},
			sand: {
				950: '#110d0b',
				900: '#211a15',
				800: '#3e3229',
				700: '#5c4b3e',
				600: '#796352',
				500: '#967b66',
				400: '#ab9481',
				300: '#bfaea0',
				200: '#d2c8bf',
				100: '#e6e2de',
				50: '#f3f1ef',
			},
			gray: {
				950: '#0d0d0d',
				900: '#1a1a1a',
				800: '#333333',
				700: '#4d4d4d',
				600: '#666666',
				500: '#808080',
				400: '#999999',
				300: '#b2b2b2',
				200: '#cccccc',
				100: '#e5e5e5',
				50: '#f2f2f2',
			},
		},
		containers: ({ theme }) => ({
			...theme("screens")
		}),
		fluid: ({ theme }) => ({
			defaultScreens: [theme("screens.mob"), theme("screens.des")],
			defaultContainers: [theme("containers.mob"), theme("containers.des")]
		}),
		// prettier-ignore
		fontFamily: {
			sans: ['avenir-next-lt-pro', ...defaultTheme.fontFamily.sans],
			serif: ['Libre Caslon Text', ...defaultTheme.fontFamily.serif],
		},
		fontSize: {
			...generateFontSize({
				12: [12, 1.2],
				13: [13, 1.2],
				14: [14, 1.4],
				16: [16, 1.5],
				17: [17, 1.5],
				18: [18, 1.5],
				20: [20, 1.4],
				22: [22, 1.4],
				24: [24, 1.4],
				26: [26, 1.4],
				28: [28, 1.4],
				30: [32, 1.4],
				32: [32, 1.4],
				34: [34, 1.4],
				36: [36, 1.4],
				39: [39, 1.2],
				40: [40, 1.2],
				47: [47, 1.2],
				48: [48, 1.2],
				56: [56, 1.2],
				60: [60, 1.2],
				64: [64, 1.2],
				72: [72, 1.2],
				80: [80, 1.2],
				88: [88, 1.2],
				96: [96, 1.2],
				100: [100, 1.2],
			}),
		},
		lineHeight: {
			100: 1,
			110: 1.1,
			120: 1.2,
			130: 1.3,
			135: 1.35,
			140: 1.4,
			150: 1.5,
			160: 1.6,
			170: 1.7,
			180: 1.8
		},
		screens: {
			mob: convertToRem(400),
			xs: convertToRem(560),
			sm: convertToRem(640),
			md: convertToRem(768),
			tab: convertToRem(960),
			lg: convertToRem(1024),
			des: convertToRem(1440),
			maxg: convertToRem(1680)
		},
		spacing: {
			inherit: "inherit",
			0: 0,
			...generateSteps(0, 1, 0.5, (val) => convertToRem(val)),
			...generateSteps(1, 20, 1, (val) => convertToRem(val)),
			...generateSteps(1, 40, 2, (val) => convertToRem(val)),
			...generateSteps(1, 80, 4, (val) => convertToRem(val)),
			...generateSteps(1, 2000, 8, (val) => convertToRem(val)),
		},
	},
	corePlugins: {
		container: false,
	},
	plugins: [
		containerQueries,
		fluid,
		plugin(({ matchUtilities, theme }) => {
			matchUtilities(
				{
					'fluid': (val) => ({
						'--fluid': val,
					}),
				},
				{
					values: theme('spacing'),
				}
			)
		}),
		forms,
		plugin(function ({ addVariant, matchVariant }) {
			addVariant("hocus", ["&:hover", "&:focus-visible"]);
			matchVariant( // https://github.com/tailwindlabs/tailwindcss/issues/12131
				"group-hocus",
				(_value, { modifier }) => {
					if (modifier) {
						return [
							`:merge(.group\\/${modifier}):hover &`,
							`:merge(.group\\/${modifier}):focus-visible &`,
						];
					} else {
						return [
							`:merge(.group):hover &`,
							`:merge(.group):focus-visible &`,
						];
					}
				},
				{
					values: {
						DEFAULT: "",
					},
				}
			);
			addVariant("hocus-within", ["&:hover", "&:has(:focus-visible)"]);
			matchVariant( // https://github.com/tailwindlabs/tailwindcss/issues/12131
				"group-hocus-within",
				(_value, { modifier }) => {
					if (modifier) {
						return [
							`:merge(.group\\/${modifier}):hover &`,
							`:merge(.group\\/${modifier}):has(:focus-visible) &`,
						];
					} else {
						return [
							`:merge(.group):hover &`,
							`:merge(.group):has(:focus-visible) &`,
						];
					}
				},
				{
					values: {
						DEFAULT: "",
					},
				}
			);
		}),
	],
};

export default config;
